import { useState, useEffect } from 'react';
import { api } from '../utils/api';

export interface StorageStats {
  usage: {
    totalSize: number;
    totalFiles: number;
    storageLimit: number | null;
    usagePercentage: number;
    available: string;
    recentFiles: number;
    trashedFiles?: number;
  };
  breakdown: {
    byType: Record<string, { size: number; count: number }>;
    byVisibility: {
      public: number;
      private: number;
    };
  };
  r2?: {
    files: number;
    size: number;
    operations: {
      classA: number;
      classB: number;
    };
    synced: boolean;
  };
}

// Owns storage-usage stats + fetch lifecycle. The consuming widget keeps the
// expanded/collapsed UI state + presentation. Behaviour mirrors the previous
// in-component logic exactly (fetch on mount, manual refresh).
export function useStorageUsage() {
  const [stats, setStats] = useState<StorageStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchStats = async () => {
    try {
      setRefreshing(true);
      setError(null);

      // ✅ Use correct token key
      const token = localStorage.getItem('vendetta-token');
      if (!token) {
        setError('Please login to view storage statistics');
        setLoading(false);
        setRefreshing(false);
        return;
      }

      const response = await api.getStorageUsage(token);
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch storage stats:', error);
      setError(error instanceof Error ? error.message : 'Failed to load storage stats');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, []);

  return { stats, loading, refreshing, error, fetchStats };
}
